/**
 * Schedule A Page — Role-Based Compensation Engine UI
 *
 * Renders one of four distinct views based on user role:
 *   - super_admin: Full waterfall + processor cost + ISO cost templates + all relationships
 *   - iso:         Own Schedule A from MSC + downline agent management + tier progress
 *   - iso_agent:   Own Schedule A from ISO + downline subagent management
 *   - iso_subagent:Simplified net-only view (Upline Gross → My Split → My Net)
 *
 * Terminology:
 *   - "Direct Upline Gross Residual"
 *   - "Your Split" / "Assigned Split"
 *   - "Your Net Residual"
 *   NEVER shows "effective entitlement %" to downlines.
 */
import React, { useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import useAuthStore from '../lib/auth-store';
import api from '../lib/api';
import MyScheduleAView from '../components/schedule_a/MyScheduleAView';
import DownlineManagerView from '../components/schedule_a/DownlineManagerView';
import ProcessorCostTemplatesView from '../components/schedule_a/ProcessorCostTemplatesView';
import IsoCostTemplatesView from '../components/schedule_a/IsoCostTemplatesView';
import { ScheduleTemplatesView } from '../components/team-economics/ScheduleTemplatesView';
import ApprovalsQueueView from '../components/schedule_a/ApprovalsQueueView';
import WaterfallView from '../components/schedule_a/WaterfallView';

export default function ScheduleAPage() {
  const { user } = useAuthStore();
  const role = user?.role;

  if (!role) return null;

  const isSuperAdmin = role === 'super_admin';
  const isIso = role === 'iso';
  const isAgent = role === 'iso_agent';
  const isSubagent = role === 'iso_subagent';

  const tabs = [];
  // MSC is the top of the line: the Super Admin's own "Schedule A" is the processor cost base, not an upline request.
  if (!isSuperAdmin) tabs.push({ value: 'my', label: 'My Schedule A', component: <MyScheduleAView /> });
  if (isSuperAdmin) tabs.push({ value: 'processor-cost', label: 'Processor Costs', component: <ProcessorCostTemplatesView /> });
  // ISO + Agent + super_admin can manage direct-child compensation
  if (isSuperAdmin || isIso || isAgent) {
    tabs.push({ value: 'downline', label: 'Downline Compensation', component: <DownlineManagerView /> });
  }
  // Approvers: anyone whose downline has a pending change
  if (isSuperAdmin || isIso || isAgent) {
    tabs.push({ value: 'approvals', label: 'Approvals', component: <ApprovalsQueueView /> });
  }
  // Super admin: cost templates + full waterfall
  if (isSuperAdmin) {
    tabs.push({ value: 'iso-cost', label: 'Schedule Templates', component: (
      <div className="space-y-8"><ScheduleTemplatesView />
        <details className="rounded-xl border p-4" data-testid="legacy-iso-cost-templates"><summary className="cursor-pointer text-sm font-medium">Legacy v1 ISO cost templates — still used by the v1 residual pages until Step 9.Z cut-over</summary><div className="mt-4"><IsoCostTemplatesView /></div></details>
      </div>) });
    tabs.push({ value: 'waterfall', label: 'Full Waterfall', component: <WaterfallView /> });
  }
  if (isIso) {
    tabs.push({ value: 'schedule-templates', label: 'Schedule Templates', component: <ScheduleTemplatesView /> });
  }

  return (
    <div className="space-y-6" data-testid="schedule-a-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" data-testid="schedule-a-title">Schedule A</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isSubagent && 'Your direct upline compensation.'}
          {isAgent && 'Your compensation from your ISO and the subagents you manage.'}
          {isIso && 'Your compensation from MSC and the agents you manage.'}
          {isSuperAdmin && 'Complete compensation architecture — processor costs, ISO pricing, downline waterfalls.'}
        </p>
      </div>

      <Tabs defaultValue={tabs[0].value} className="w-full">
        <TabsList className="w-full justify-start overflow-x-auto flex-nowrap" data-testid="schedule-a-tabs">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value} data-testid={`schedule-a-tab-${t.value}`}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {tabs.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-6">
            {t.component}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
